import { definePlatformSetting, } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { projectsCloseoutConfigurationSchema } from '../contracts/projects-project-closeout.contract.js';
import { projectsProjectFileContentTypeSchema } from '../contracts/projects-project-file.contract.js';
import { projectsPriorityKeySchema, projectsProjectTypeKeySchema, projectsPropertyTypeKeySchema, projectsStatusKeySchema, } from '../contracts/projects-project.contract.js';
const projectsBoardSectionSchema = z.strictObject({
    key: projectsStatusKeySchema,
    label: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(240),
});
const projectsBoardStageSchema = z.strictObject({
    key: projectsStatusKeySchema,
    label: z.string().trim().min(1).max(60),
    accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    terminal: z.boolean(),
    requiresCloseout: z.boolean().optional(),
    visible: z.boolean().optional(),
    allowManualTransition: z.boolean().optional(),
    sectionKey: projectsStatusKeySchema.optional(),
    workInProgressLimit: z.number().int().min(1).max(500).nullable(),
});
const projectsBoardConfigurationV1Schema = z.strictObject({
    stages: z.array(z.strictObject({
        key: projectsStatusKeySchema,
        label: z.string().trim().min(1).max(60),
        accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
        terminal: z.boolean(),
        workInProgressLimit: z.number().int().min(1).max(500).nullable(),
    })).min(1).max(40),
    defaultStage: projectsStatusKeySchema,
    showProjectNumbers: z.boolean(),
});
function projectsMigrateBoardConfigurationV1(value) {
    const previous = projectsBoardConfigurationV1Schema.parse(value);
    const sectionKey = 'other_active_work';
    return {
        schemaVersion: 2,
        sections: [{
                key: sectionKey,
                label: 'Migrated workflow',
                description: 'Project stages retained from an earlier tenant Board configuration.',
            }],
        stages: previous.stages.map((stage) => ({
            ...stage,
            requiresCloseout: stage.terminal,
            visible: true,
            allowManualTransition: true,
            sectionKey,
        })),
        defaultStage: previous.defaultStage,
        showProjectNumbers: previous.showProjectNumbers,
        showTerminalStagesByDefault: true,
    };
}
const projectsPreserveSettingValue = (value) => value;
export const projectsBoardConfigurationValueSchema = z.strictObject({
    schemaVersion: z.number().int().min(1).max(2).optional(),
    sections: z.array(projectsBoardSectionSchema).min(1).max(12).optional(),
    stages: z.array(projectsBoardStageSchema).min(1).max(40),
    defaultStage: projectsStatusKeySchema,
    showProjectNumbers: z.boolean(),
    showTerminalStagesByDefault: z.boolean().optional(),
}).superRefine((board, context) => {
    const sectionKeys = new Set();
    for (const [index, section] of (board.sections ?? []).entries()) {
        if (sectionKeys.has(section.key)) {
            context.addIssue({
                code: 'custom',
                path: ['sections', index, 'key'],
                message: `Duplicate Project board section key: ${section.key}`,
            });
        }
        sectionKeys.add(section.key);
    }
    const keys = new Set();
    for (const [index, stage] of board.stages.entries()) {
        if (keys.has(stage.key)) {
            context.addIssue({
                code: 'custom',
                path: ['stages', index, 'key'],
                message: `Duplicate Project stage key: ${stage.key}`,
            });
        }
        keys.add(stage.key);
        if (stage.sectionKey !== undefined && board.sections !== undefined && !sectionKeys.has(stage.sectionKey)) {
            context.addIssue({
                code: 'custom',
                path: ['stages', index, 'sectionKey'],
                message: `Project stage section does not exist: ${stage.sectionKey}`,
            });
        }
        const requiresCloseout = stage.requiresCloseout ?? stage.terminal;
        if (requiresCloseout && !stage.terminal) {
            context.addIssue({
                code: 'custom',
                path: ['stages', index, 'requiresCloseout'],
                message: 'A Project closeout stage must also be terminal.',
            });
        }
        if (requiresCloseout && stage.visible === false) {
            context.addIssue({
                code: 'custom',
                path: ['stages', index, 'visible'],
                message: 'A Project closeout stage must remain visible to authorized users.',
            });
        }
        if (requiresCloseout && stage.allowManualTransition === false) {
            context.addIssue({
                code: 'custom',
                path: ['stages', index, 'allowManualTransition'],
                message: 'A Project closeout stage must remain selectable by authorized users.',
            });
        }
    }
    const defaultStage = board.stages.find(({ key }) => key === board.defaultStage);
    if (defaultStage === undefined) {
        context.addIssue({
            code: 'custom',
            path: ['defaultStage'],
            message: 'The default Project stage must exist in the stage list.',
        });
    }
    else if (defaultStage.terminal) {
        context.addIssue({
            code: 'custom',
            path: ['defaultStage'],
            message: 'The default Project stage cannot be terminal.',
        });
    }
    else if (defaultStage.visible === false || defaultStage.allowManualTransition === false) {
        context.addIssue({
            code: 'custom',
            path: ['defaultStage'],
            message: 'The default Project stage must be visible and allow ordinary transitions.',
        });
    }
});
const projectsHexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
function projectsRelativeLuminance(hex) {
    const channels = [1, 3, 5].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255);
    const [red, green, blue] = channels.map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4));
    return (0.2126 * red) + (0.7152 * green) + (0.0722 * blue);
}
function projectsContrastRatio(first, second) {
    const firstLuminance = projectsRelativeLuminance(first);
    const secondLuminance = projectsRelativeLuminance(second);
    return (Math.max(firstLuminance, secondLuminance) + 0.05) /
        (Math.min(firstLuminance, secondLuminance) + 0.05);
}
const projectsBoardPaletteSchema = z.strictObject({
    shellSurface: projectsHexColorSchema,
    sectionSurface: projectsHexColorSchema,
    laneSurface: projectsHexColorSchema,
    cardSurface: projectsHexColorSchema,
    text: projectsHexColorSchema,
    textMuted: projectsHexColorSchema,
    border: projectsHexColorSchema,
}).superRefine((palette, context) => {
    const surfaces = [
        palette.shellSurface,
        palette.sectionSurface,
        palette.laneSurface,
        palette.cardSurface,
    ];
    if (surfaces.some((surface) => projectsContrastRatio(palette.text, surface) < 4.5)) {
        context.addIssue({
            code: 'custom',
            path: ['text'],
            message: 'Project Board text must maintain at least 4.5:1 contrast on every Board surface.',
        });
    }
    if (surfaces.some((surface) => projectsContrastRatio(palette.textMuted, surface) < 3)) {
        context.addIssue({
            code: 'custom',
            path: ['textMuted'],
            message: 'Muted Project Board text must maintain at least 3:1 contrast on every Board surface.',
        });
    }
});
export const projectsBoardAppearanceValueSchema = z.strictObject({
    light: projectsBoardPaletteSchema,
    dark: projectsBoardPaletteSchema,
    geometry: z.strictObject({
        laneMinWidthPx: z.number().int().min(220).max(520),
        laneGapPx: z.number().int().min(4).max(40),
        cardMinHeightPx: z.number().int().min(80).max(360),
    }),
});
export const projectsOperationalConfigurationValueSchema = z.strictObject({
    schemaVersion: z.literal(1),
    projectTypes: z.array(z.strictObject({
        key: projectsProjectTypeKeySchema,
        label: z.string().trim().min(1).max(80),
    })).min(1).max(40),
    defaultProjectType: projectsProjectTypeKeySchema,
    propertyTypes: z.array(z.strictObject({
        key: projectsPropertyTypeKeySchema,
        label: z.string().trim().min(1).max(80),
    })).min(1).max(20),
    priorities: z.array(z.strictObject({
        key: projectsPriorityKeySchema,
        label: z.string().trim().min(1).max(80),
        accent: projectsHexColorSchema,
    })).length(4),
}).superRefine((operations, context) => {
    const typeKeys = new Set();
    for (const [index, projectType] of operations.projectTypes.entries()) {
        if (typeKeys.has(projectType.key)) {
            context.addIssue({
                code: 'custom',
                path: ['projectTypes', index, 'key'],
                message: `Duplicate Project type key: ${projectType.key}`,
            });
        }
        typeKeys.add(projectType.key);
    }
    if (!typeKeys.has(operations.defaultProjectType)) {
        context.addIssue({
            code: 'custom',
            path: ['defaultProjectType'],
            message: 'The default Project type must exist in the configured type list.',
        });
    }
    const propertyKeys = new Set();
    for (const [index, propertyType] of operations.propertyTypes.entries()) {
        if (propertyKeys.has(propertyType.key)) {
            context.addIssue({
                code: 'custom',
                path: ['propertyTypes', index, 'key'],
                message: `Duplicate Project property type key: ${propertyType.key}`,
            });
        }
        propertyKeys.add(propertyType.key);
    }
    const priorityKeys = new Set(operations.priorities.map(({ key }) => key));
    for (const required of projectsPriorityKeySchema.options) {
        if (!priorityKeys.has(required)) {
            context.addIssue({
                code: 'custom',
                path: ['priorities'],
                message: `Project priorities must include the ${required} semantic key exactly once.`,
            });
        }
    }
});
export const projectsOperationalConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'projects.operations.configuration',
        moduleId: 'projects',
        title: 'Project operational fields',
        helpText: 'Controls tenant Project type and property type keys/labels, the default Project type, and priority labels/colors/order.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            schemaVersion: 1,
            projectTypes: [
                { key: 'mitigation', label: 'Mitigation' },
                { key: 'carpet_cleaning', label: 'Carpet Cleaning' },
                { key: 'biohazard', label: 'Biohazard' },
                { key: 'reconstruction', label: 'Reconstruction' },
                { key: 'mold', label: 'Mold' },
                { key: 'contents', label: 'Contents' },
                { key: 'fire', label: 'Fire' },
                { key: 'other', label: 'Other' },
            ],
            defaultProjectType: 'other',
            propertyTypes: [
                { key: 'residential', label: 'Residential' },
                { key: 'commercial', label: 'Commercial' },
            ],
            priorities: [
                { key: 'low', label: 'Low', accent: '#64748b' },
                { key: 'normal', label: 'Normal', accent: '#3b82f6' },
                { key: 'high', label: 'High', accent: '#f59e0b' },
                { key: 'emergency', label: 'Emergency', accent: '#ef4444' },
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'operational-reference',
    },
    valueSchema: projectsOperationalConfigurationValueSchema,
});
export function projectsStageRequiresCloseout(stage) {
    return stage.requiresCloseout ?? stage.terminal;
}
export function projectsStageIsVisible(stage) {
    return stage.visible ?? true;
}
export function projectsStageAllowsManualTransition(stage) {
    return stage.allowManualTransition ?? true;
}
function projectsCopyTemplateSchema(requiredPlaceholders, maximumLength) {
    return z.string().trim().min(1).max(maximumLength).superRefine((value, context) => {
        const placeholders = [...value.matchAll(/\{[a-z][a-zA-Z0-9]*\}/g)].map(([placeholder]) => placeholder);
        for (const placeholder of requiredPlaceholders) {
            if (!placeholders.includes(placeholder)) {
                context.addIssue({
                    code: 'custom',
                    message: `Project copy template must include ${placeholder}.`,
                });
            }
        }
        for (const placeholder of placeholders) {
            if (!requiredPlaceholders.includes(placeholder)) {
                context.addIssue({
                    code: 'custom',
                    message: `Project copy template uses unsupported placeholder ${placeholder}.`,
                });
            }
        }
    });
}
export const projectsCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(80),
    createAction: z.string().trim().min(1).max(80),
    emptyTitle: z.string().trim().min(1).max(120),
    emptyDescription: z.string().trim().min(1).max(240),
    filteredEmptyTitle: z.string().trim().min(1).max(120),
    filteredEmptyDescription: z.string().trim().min(1).max(240),
    searchLabel: z.string().trim().min(1).max(80),
    searchPlaceholder: z.string().trim().min(1).max(120),
    statusFilterLabel: z.string().trim().min(1).max(80),
    allStatusesLabel: z.string().trim().min(1).max(80),
    clearFiltersAction: z.string().trim().min(1).max(80),
    listFiltersRegionLabel: z.string().trim().min(1).max(120),
    resultsSummary: projectsCopyTemplateSchema(['{total}'], 160),
    paginationRegionLabel: z.string().trim().min(1).max(120),
    pageRangeSummary: projectsCopyTemplateSchema(['{first}', '{last}', '{total}'], 160),
    previousPageAction: z.string().trim().min(1).max(80),
    nextPageAction: z.string().trim().min(1).max(80),
    detailContributionLoading: z.string().trim().min(1).max(160),
    detailContributionError: z.string().trim().min(1).max(200),
    createError: z.string().trim().min(1).max(240),
    projectNumberLabel: z.string().trim().min(1).max(80),
    projectNameLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    backToProjects: z.string().trim().min(1).max(80),
    customerSectionTitle: z.string().trim().min(1).max(120),
    serviceLocationSectionTitle: z.string().trim().min(1).max(120),
    editProjectAction: z.string().trim().min(1).max(80).default('Edit project'),
    saveProjectAction: z.string().trim().min(1).max(80),
    saveProjectError: z.string().trim().min(1).max(240),
    revisionConflictError: z.string().trim().min(1).max(240),
    archiveRequiredNoteError: z.string().trim().min(1).max(240),
    immutableNoteError: z.string().trim().min(1).max(240),
    notesSectionTitle: z.string().trim().min(1).max(120),
    timelineSectionTitle: z.string().trim().min(1).max(120),
    addNoteAction: z.string().trim().min(1).max(80),
    saveNoteAction: z.string().trim().min(1).max(80),
    deleteNoteAction: z.string().trim().min(1).max(80),
    noNotesLabel: z.string().trim().min(1).max(160),
    noTimelineLabel: z.string().trim().min(1).max(160),
    filesSectionTitle: z.string().trim().min(1).max(120),
    filesDescription: z.string().trim().min(1).max(300),
    filesEmptyLabel: z.string().trim().min(1).max(160),
    filesLoadingLabel: z.string().trim().min(1).max(160),
    filesShowDeletedLabel: z.string().trim().min(1).max(120),
    fileSelectLabel: z.string().trim().min(1).max(80),
    fileCapturePhotoAction: z.string().trim().min(1).max(80),
    fileRoomLabel: z.string().trim().min(1).max(80),
    filePhaseLabel: z.string().trim().min(1).max(80),
    filePhaseNoneLabel: z.string().trim().min(1).max(80),
    filePhaseBeforeLabel: z.string().trim().min(1).max(80),
    filePhaseDuringLabel: z.string().trim().min(1).max(80),
    filePhaseAfterLabel: z.string().trim().min(1).max(80),
    filePhaseOtherLabel: z.string().trim().min(1).max(80),
    fileUploadAction: z.string().trim().min(1).max(80),
    fileCancelUploadAction: z.string().trim().min(1).max(80),
    fileHashingStatus: projectsCopyTemplateSchema(['{percent}'], 160),
    fileUploadingStatus: projectsCopyTemplateSchema(['{percent}'], 160),
    fileFinalizingStatus: z.string().trim().min(1).max(160),
    fileLoadError: z.string().trim().min(1).max(240),
    fileUploadError: z.string().trim().min(1).max(240),
    fileVerificationError: z.string().trim().min(1).max(240),
    fileTypeNotAllowedError: z.string().trim().min(1).max(240),
    fileTooLargeError: z.string().trim().min(1).max(240),
    fileDownloadAction: z.string().trim().min(1).max(80),
    fileDownloadError: z.string().trim().min(1).max(240),
    fileDeleteAction: z.string().trim().min(1).max(80),
    fileDeleteConfirmation: z.string().trim().min(1).max(240),
    fileRestoreAction: z.string().trim().min(1).max(80),
    fileMutationError: z.string().trim().min(1).max(240),
    fileDeletedStatus: z.string().trim().min(1).max(80),
    fileSizeSummary: projectsCopyTemplateSchema(['{size}'], 120),
    teamSectionTitle: z.string().trim().min(1).max(120),
    teamEmptyLabel: z.string().trim().min(1).max(160),
    teamInactiveMemberLabel: z.string().trim().min(1).max(120),
    teamAssignAction: z.string().trim().min(1).max(80),
    teamAssignTitle: z.string().trim().min(1).max(120),
    teamAssignDescription: z.string().trim().min(1).max(300),
    teamSearchLabel: z.string().trim().min(1).max(80),
    teamSearchPlaceholder: z.string().trim().min(1).max(160),
    teamMembersLabel: z.string().trim().min(1).max(80),
    teamRoleLabel: z.string().trim().min(1).max(80),
    teamRoleHelp: z.string().trim().min(1).max(240),
    teamTechnicianRoleLabel: z.string().trim().min(1).max(80),
    teamLeadRoleLabel: z.string().trim().min(1).max(80),
    teamSalesRoleLabel: z.string().trim().min(1).max(80),
    teamNoAvailableLabel: z.string().trim().min(1).max(160),
    teamNoMatchesLabel: z.string().trim().min(1).max(160),
    teamSelectedSummary: projectsCopyTemplateSchema(['{count}'], 120),
    teamSelectFilteredAction: z.string().trim().min(1).max(80),
    teamClearFilteredAction: z.string().trim().min(1).max(80),
    teamSubmitSummary: projectsCopyTemplateSchema(['{count}'], 120),
    teamRemoveAction: z.string().trim().min(1).max(80),
    teamLoadError: z.string().trim().min(1).max(240),
    teamSaveError: z.string().trim().min(1).max(240),
    noteBodyLabel: z.string().trim().min(1).max(80),
    customerNameLabel: z.string().trim().min(1).max(80),
    customerPhoneLabel: z.string().trim().min(1).max(80),
    customerEmailLabel: z.string().trim().min(1).max(80),
    customerAddressLabel: z.string().trim().min(1).max(80),
    serviceAddressLabel: z.string().trim().min(1).max(80),
    addressLine2Label: z.string().trim().min(1).max(80),
    cityLabel: z.string().trim().min(1).max(80),
    regionLabel: z.string().trim().min(1).max(80),
    postalCodeLabel: z.string().trim().min(1).max(80),
    pinnedLabel: z.string().trim().min(1).max(80),
    saveNoteError: z.string().trim().min(1).max(240),
    closeoutAction: z.string().trim().min(1).max(80),
    closeoutTitle: z.string().trim().min(1).max(120),
    closeoutDescription: z.string().trim().min(1).max(300),
    closeoutTargetLabel: z.string().trim().min(1).max(80),
    closeoutOverrideLabel: z.string().trim().min(1).max(80),
    closeoutOverrideHelp: z.string().trim().min(1).max(240),
    closeoutSubmitAction: z.string().trim().min(1).max(80),
    closeoutLoadError: z.string().trim().min(1).max(240),
    closeoutIncompleteError: z.string().trim().min(1).max(240),
    closeoutProviderUnavailable: z.string().trim().min(1).max(240),
    cancelAction: z.string().trim().min(1).max(80),
    boardNavigationLabel: z.string().trim().min(1).max(60),
    boardTitle: z.string().trim().min(1).max(100),
    boardDescription: z.string().trim().min(1).max(300),
    boardJumpLabel: z.string().trim().min(1).max(80),
    boardShowTerminalLabel: z.string().trim().min(1).max(100),
    boardMoveLabel: z.string().trim().min(1).max(80),
    boardOpenProjectLabel: z.string().trim().min(1).max(80),
    boardEmptyStageLabel: z.string().trim().min(1).max(160),
    boardLoadError: z.string().trim().min(1).max(240),
    boardMoveError: z.string().trim().min(1).max(240),
    boardRevisionConflictError: z.string().trim().min(1).max(240),
    boardUnconfiguredSectionLabel: z.string().trim().min(1).max(100),
    boardUnconfiguredSectionDescription: z.string().trim().min(1).max(240),
    createTitle: z.string().trim().min(1).max(80),
    createAdvancedLabel: z.string().trim().min(1).max(80),
    projectTypeLabel: z.string().trim().min(1).max(80),
    priorityLabel: z.string().trim().min(1).max(80),
    propertyTypeLabel: z.string().trim().min(1).max(80),
    propertyTypeNoneLabel: z.string().trim().min(1).max(80),
    waterJobLabel: z.string().trim().min(1).max(80),
    waterJobHelp: z.string().trim().min(1).max(240),
    startDateLabel: z.string().trim().min(1).max(80),
    projectNumberOptionalHelp: z.string().trim().min(1).max(240),
    contactActionsLabel: z.string().trim().min(1).max(80),
    callAction: z.string().trim().min(1).max(80),
    textAction: z.string().trim().min(1).max(80),
    emailAction: z.string().trim().min(1).max(80),
    mapAction: z.string().trim().min(1).max(80),
    filesAction: z.string().trim().min(1).max(80),
    updatedAtSummary: projectsCopyTemplateSchema(['{date}'], 120),
});
export const projectsBoardConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'projects.board.configuration',
        moduleId: 'projects',
        title: 'Project board',
        helpText: 'Controls tenant project stages, labels, accents, and board behavior.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            schemaVersion: 2,
            sections: [
                {
                    key: 'intake_and_decision',
                    label: '1. Intake & customer decision',
                    description: 'Qualify the job, prepare and send the estimate, then resolve the customer decision.',
                },
                {
                    key: 'planning_and_production',
                    label: '2. Planning & production',
                    description: 'Schedule approved work, prepare materials, complete field work, drying, and quality review.',
                },
                {
                    key: 'financial_closeout_and_records',
                    label: '3. Financial closeout & records',
                    description: 'Invoice, collect payment, then retain completed, closed, or cancelled records.',
                },
                {
                    key: 'other_active_work',
                    label: 'Other active work',
                    description: 'Custom workflow stages used by this tenant.',
                },
            ],
            stages: [
                { key: 'new', label: 'New', accent: '#3b82f6', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'estimating', label: 'Estimating', accent: '#14b8a6', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'estimate_sent', label: 'Estimate Sent', accent: '#6366f1', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'waiting_customer', label: 'Waiting on Customer', accent: '#f59e0b', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'follow_up_required', label: 'Follow-Up Required', accent: '#ec4899', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'on_hold', label: 'On Hold', accent: '#64748b', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'intake_and_decision', workInProgressLimit: null },
                { key: 'ready_to_schedule', label: 'Ready to Schedule', accent: '#10b981', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'scheduled', label: 'Scheduled', accent: '#a855f7', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'materials_ordered', label: 'Materials Ordered', accent: '#8b5cf6', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'in_progress', label: 'In Progress', accent: '#eab308', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'drying', label: 'Drying', accent: '#22d3ee', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'quality_review', label: 'Quality Review', accent: '#06b6d4', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'planning_and_production', workInProgressLimit: null },
                { key: 'pending_invoice', label: 'Pending Invoice', accent: '#f97316', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'financial_closeout_and_records', workInProgressLimit: null },
                { key: 'pending_payment', label: 'Pending Payment', accent: '#ef4444', terminal: false, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'financial_closeout_and_records', workInProgressLimit: null },
                { key: 'completed', label: 'Completed', accent: '#22c55e', terminal: true, requiresCloseout: true, visible: true, allowManualTransition: true, sectionKey: 'financial_closeout_and_records', workInProgressLimit: null },
                { key: 'closed', label: 'Closed', accent: '#94a3b8', terminal: true, requiresCloseout: true, visible: true, allowManualTransition: true, sectionKey: 'financial_closeout_and_records', workInProgressLimit: null },
                { key: 'cancelled', label: 'Cancelled', accent: '#475569', terminal: true, requiresCloseout: false, visible: true, allowManualTransition: true, sectionKey: 'financial_closeout_and_records', workInProgressLimit: null },
                { key: 'legacy_import', label: 'Legacy Import', accent: '#64748b', terminal: true, requiresCloseout: false, visible: false, allowManualTransition: false, sectionKey: 'other_active_work', workInProgressLimit: null },
            ],
            defaultStage: 'new',
            showProjectNumbers: true,
            showTerminalStagesByDefault: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'operational-reference',
    },
    valueSchema: projectsBoardConfigurationValueSchema,
    migrations: [
        { fromVersion: 1, toVersion: 2, migrate: projectsMigrateBoardConfigurationV1 },
    ],
});
const projectsCopyCodedFallback = {
    title: 'Projects',
    createAction: 'Create project',
    emptyTitle: 'No projects yet',
    emptyDescription: 'Create the first project for this tenant.',
    filteredEmptyTitle: 'No matching projects',
    filteredEmptyDescription: 'Adjust the search or stage filter to see more projects.',
    searchLabel: 'Search projects',
    searchPlaceholder: 'Search job number, name, customer, or address',
    statusFilterLabel: 'Project stage',
    allStatusesLabel: 'All stages',
    clearFiltersAction: 'Clear filters',
    listFiltersRegionLabel: 'Project list filters',
    resultsSummary: '{total} projects',
    paginationRegionLabel: 'Project list pages',
    pageRangeSummary: '{first}–{last} of {total}',
    previousPageAction: 'Previous page',
    nextPageAction: 'Next page',
    detailContributionLoading: 'Loading project module panel…',
    detailContributionError: 'A project module panel is temporarily unavailable.',
    createError: 'The project could not be created.',
    projectNumberLabel: 'Project number',
    projectNameLabel: 'Project name',
    statusLabel: 'Status',
    backToProjects: 'Back to projects',
    customerSectionTitle: 'Customer snapshot',
    serviceLocationSectionTitle: 'Service location',
    editProjectAction: 'Edit project',
    saveProjectAction: 'Save project',
    saveProjectError: 'The project could not be saved.',
    revisionConflictError: 'This record changed in another session. Refresh and try again.',
    archiveRequiredNoteError: 'This Assistant-created note must be archived through an approved Assistant action instead of deleted.',
    immutableNoteError: 'This Assistant-created note is retained as approved history and cannot be edited. Archive it through an approved Assistant action if it should no longer appear in current notes.',
    notesSectionTitle: 'Project notes',
    timelineSectionTitle: 'Activity timeline',
    addNoteAction: 'Add note',
    saveNoteAction: 'Save note',
    deleteNoteAction: 'Delete note',
    noNotesLabel: 'No project notes yet.',
    noTimelineLabel: 'No project activity yet.',
    filesSectionTitle: 'Project files and photos',
    filesDescription: 'Add verified photos, videos, and documents to this project record.',
    filesEmptyLabel: 'No project files yet.',
    filesLoadingLabel: 'Loading project files…',
    filesShowDeletedLabel: 'Show deleted files',
    fileSelectLabel: 'Choose a file',
    fileCapturePhotoAction: 'Take photo',
    fileRoomLabel: 'Room or area',
    filePhaseLabel: 'Work phase',
    filePhaseNoneLabel: 'Not specified',
    filePhaseBeforeLabel: 'Before',
    filePhaseDuringLabel: 'During',
    filePhaseAfterLabel: 'After',
    filePhaseOtherLabel: 'Other',
    fileUploadAction: 'Upload file',
    fileCancelUploadAction: 'Cancel upload',
    fileHashingStatus: 'Preparing file securely: {percent}%',
    fileUploadingStatus: 'Uploading: {percent}%',
    fileFinalizingStatus: 'Verifying file before attachment…',
    fileLoadError: 'Project files could not be loaded.',
    fileUploadError: 'The project file could not be uploaded.',
    fileVerificationError: 'The uploaded file did not pass verification.',
    fileTypeNotAllowedError: 'This file type is not allowed by the tenant file settings.',
    fileTooLargeError: 'This file is larger than the tenant file-size limit.',
    fileDownloadAction: 'Download',
    fileDownloadError: 'A secure download link could not be created.',
    fileDeleteAction: 'Delete',
    fileDeleteConfirmation: 'Move this file to deleted files? It can be restored before its retention period ends.',
    fileRestoreAction: 'Restore',
    fileMutationError: 'The project file changed or could not be updated.',
    fileDeletedStatus: 'Deleted',
    fileSizeSummary: '{size}',
    teamSectionTitle: 'Assigned team',
    teamEmptyLabel: 'No team members are assigned yet.',
    teamInactiveMemberLabel: 'Inactive team member',
    teamAssignAction: 'Assign team',
    teamAssignTitle: 'Assign team to project',
    teamAssignDescription: 'Search and select multiple team members, then assign the role they fill on this project.',
    teamSearchLabel: 'Search team',
    teamSearchPlaceholder: 'Search by name',
    teamMembersLabel: 'Team members',
    teamRoleLabel: 'Role on project',
    teamRoleHelp: 'Choose the operational role each selected person fills on this project.',
    teamTechnicianRoleLabel: 'Technician',
    teamLeadRoleLabel: 'Crew lead',
    teamSalesRoleLabel: 'Sales representative',
    teamNoAvailableLabel: 'Every active team member is already assigned.',
    teamNoMatchesLabel: 'No matching team members were found.',
    teamSelectedSummary: '{count} selected',
    teamSelectFilteredAction: 'Select filtered',
    teamClearFilteredAction: 'Clear filtered',
    teamSubmitSummary: 'Assign {count}',
    teamRemoveAction: 'Remove assignment',
    teamLoadError: 'The assigned team could not be loaded.',
    teamSaveError: 'The project team could not be updated.',
    noteBodyLabel: 'Note',
    customerNameLabel: 'Customer name',
    customerPhoneLabel: 'Customer phone',
    customerEmailLabel: 'Customer email',
    customerAddressLabel: 'Customer address',
    serviceAddressLabel: 'Service address',
    addressLine2Label: 'Address line 2',
    cityLabel: 'City',
    regionLabel: 'State or region',
    postalCodeLabel: 'Postal code',
    pinnedLabel: 'Pinned',
    saveNoteError: 'The project note could not be saved.',
    closeoutAction: 'Close project',
    closeoutTitle: 'Project closeout',
    closeoutDescription: 'Complete the checklist before moving this project to a terminal stage.',
    closeoutTargetLabel: 'Closeout status',
    closeoutOverrideLabel: 'Override reason',
    closeoutOverrideHelp: 'A reason of at least 10 characters is required for incomplete checks.',
    closeoutSubmitAction: 'Complete closeout',
    closeoutLoadError: 'Closeout checks could not be loaded.',
    closeoutIncompleteError: 'Complete every required check or enter an authorized override reason.',
    closeoutProviderUnavailable: 'A closeout check is unavailable. Try again before closing this project.',
    cancelAction: 'Cancel',
    boardNavigationLabel: 'Job Board',
    boardTitle: 'Job Board',
    boardDescription: 'Every job stage in one organized workflow from intake through closeout.',
    boardJumpLabel: 'Jump to section',
    boardShowTerminalLabel: 'Show completed, closed, and cancelled stages',
    boardMoveLabel: 'Move to',
    boardOpenProjectLabel: 'Open project',
    boardEmptyStageLabel: 'No projects in this stage.',
    boardLoadError: 'The Job Board could not be loaded.',
    boardMoveError: 'The project could not be moved.',
    boardRevisionConflictError: 'This project changed in another session. The Board has been refreshed.',
    boardUnconfiguredSectionLabel: 'Unconfigured project stages',
    boardUnconfiguredSectionDescription: 'Projects with retained statuses that are not yet mapped in this tenant Board configuration.',
    createTitle: 'New project',
    createAdvancedLabel: 'Advanced options',
    projectTypeLabel: 'Project type',
    priorityLabel: 'Priority',
    propertyTypeLabel: 'Property type',
    propertyTypeNoneLabel: 'Not specified',
    waterJobLabel: 'Water job',
    waterJobHelp: 'Track water-damage compliance requirements for this project.',
    startDateLabel: 'Start date',
    projectNumberOptionalHelp: 'Leave blank to assign the next number automatically.',
    contactActionsLabel: 'Contact actions',
    callAction: 'Call',
    textAction: 'Text',
    emailAction: 'Email',
    mapAction: 'Map',
    filesAction: 'Files',
    updatedAtSummary: 'Updated {date}',
};
/**
 * Copy versions 11 and 12 add create/contact/list labels and explicit Project editing.
 * Stored tenant copy stays on top of the fallback so existing wording is preserved.
 */
function projectsMigrateCopyV10(value) {
    const stored = value !== null && typeof value === 'object' && !Array.isArray(value) ? value : {};
    return { ...projectsCopyCodedFallback, ...stored };
}
export const projectsCopySetting = definePlatformSetting({
    definition: {
        key: 'projects.copy.default',
        moduleId: 'projects',
        title: 'Projects interface copy',
        helpText: 'Controls tenant-editable labels and empty states in Projects.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 12,
        codedFallback: projectsCopyCodedFallback,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: projectsCopyValueSchema,
    migrations: [
        { fromVersion: 1, toVersion: 2, migrate: projectsPreserveSettingValue },
        { fromVersion: 2, toVersion: 3, migrate: projectsPreserveSettingValue },
        { fromVersion: 3, toVersion: 4, migrate: projectsPreserveSettingValue },
        { fromVersion: 4, toVersion: 5, migrate: projectsPreserveSettingValue },
        { fromVersion: 5, toVersion: 6, migrate: projectsPreserveSettingValue },
        { fromVersion: 6, toVersion: 7, migrate: projectsPreserveSettingValue },
        { fromVersion: 7, toVersion: 8, migrate: projectsPreserveSettingValue },
        { fromVersion: 8, toVersion: 9, migrate: projectsPreserveSettingValue },
        { fromVersion: 9, toVersion: 10, migrate: projectsPreserveSettingValue },
        { fromVersion: 10, toVersion: 11, migrate: projectsMigrateCopyV10 },
        { fromVersion: 11, toVersion: 12, migrate: projectsMigrateCopyV10 },
    ],
});
export const projectsBoardAppearanceSetting = definePlatformSetting({
    definition: {
        key: 'projects.board.appearance',
        moduleId: 'projects',
        title: 'Project board appearance',
        helpText: 'Controls tenant Job Board surfaces, text contrast, lane size, and card height for light and dark displays.',
        configurationDomain: 'theme',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            light: {
                shellSurface: '#D8EBF6',
                sectionSurface: '#F2F8FC',
                laneSurface: '#E9F3F9',
                cardSurface: '#FFFFFF',
                text: '#172033',
                textMuted: '#526477',
                border: '#B8D0DE',
            },
            dark: {
                shellSurface: '#091B2A',
                sectionSurface: '#102B3E',
                laneSurface: '#0D2334',
                cardSurface: '#17384D',
                text: '#F2FAFF',
                textMuted: '#B8CBD8',
                border: '#28526A',
            },
            geometry: { laneMinWidthPx: 260, laneGapPx: 16, cardMinHeightPx: 128 },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: projectsBoardAppearanceValueSchema,
});
export const projectsCloseoutConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'projects.closeout.configuration',
        moduleId: 'projects',
        title: 'Project closeout checklist',
        helpText: 'Controls the tenant-owned manual checks required before a Project enters a terminal stage.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            schemaVersion: 1,
            items: [
                { key: 'project_record_reviewed', label: 'Project record and final scope reviewed', required: true },
                { key: 'service_location_safe', label: 'Service location left safe and documented', required: true },
                { key: 'customer_handoff_confirmed', label: 'Customer closeout handoff decision recorded', required: true },
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'operational-reference',
    },
    valueSchema: projectsCloseoutConfigurationSchema,
});
export const projectsFileConfigurationValueSchema = z.strictObject({
    schemaVersion: z.literal(1),
    maxFileSizeBytes: z.number().int().min(1_024).max(5 * 1_024 * 1_024 * 1_024),
    softDeleteDays: z.number().int().min(1).max(365),
    uploadAuthorizationSeconds: z.number().int().min(60).max(1_800),
    downloadAuthorizationSeconds: z.number().int().min(15).max(300),
    allowedContentTypes: z.array(projectsProjectFileContentTypeSchema).min(1).max(20).superRefine((values, context) => {
        if (new Set(values).size !== values.length) {
            context.addIssue({ code: 'custom', message: 'Project file content types must be unique.' });
        }
    }),
});
export const projectsFileConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'projects.files.configuration',
        moduleId: 'projects',
        title: 'Project files',
        helpText: 'Controls safe tenant file types, file-size bounds, signed-link lifetimes, and soft-delete retention.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            schemaVersion: 1,
            maxFileSizeBytes: 5 * 1_024 * 1_024 * 1_024,
            softDeleteDays: 30,
            uploadAuthorizationSeconds: 900,
            downloadAuthorizationSeconds: 60,
            allowedContentTypes: [
                'image/heic',
                'image/heif',
                'image/jpeg',
                'image/png',
                'image/webp',
                'video/mp4',
                'video/quicktime',
                'application/pdf',
                'text/plain',
                'text/csv',
                'application/msword',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'operational-reference',
    },
    valueSchema: projectsFileConfigurationValueSchema,
});
export const projectsPortalDisplayValueSchema = z.strictObject({
    enabled: z.boolean(),
    showCustomerName: z.boolean(),
    showServiceAddress: z.boolean(),
    showStatus: z.boolean(),
});
export const projectsPortalDisplaySetting = definePlatformSetting({
    definition: {
        key: 'projects.display.portal', moduleId: 'projects', title: 'Project Portal summary display',
        helpText: 'Controls which customer-safe Project summary fields appear in the Customer Portal.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            enabled: true, showCustomerName: true, showServiceAddress: true, showStatus: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: projectsPortalDisplayValueSchema,
});
export const projectsPortalCopyValueSchema = z.strictObject({
    projectLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    customerLabel: z.string().trim().min(1).max(80),
    serviceAddressLabel: z.string().trim().min(1).max(80),
});
export const projectsPortalCopySetting = definePlatformSetting({
    definition: {
        key: 'projects.copy.portal', moduleId: 'projects', title: 'Project Portal summary copy',
        helpText: 'Controls tenant-editable labels in the Projects-owned Customer Portal panel.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            projectLabel: 'Project', statusLabel: 'Status', customerLabel: 'Customer',
            serviceAddressLabel: 'Service address',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: projectsPortalCopyValueSchema,
});
export const projectsProjectDetailLauncherSizeSchema = z.enum([
    'compact',
    'standard',
    'wide',
    'large',
]);
const projectsProjectDetailLauncherModuleIdSchema = z.string()
    .regex(/^[a-z][a-z0-9-]{0,79}$/);
const projectsProjectDetailLauncherRuleSchema = z.strictObject({
    moduleId: projectsProjectDetailLauncherModuleIdSchema,
    tenantVisible: z.boolean(),
    userCanHide: z.boolean(),
    defaultOrder: z.number().int().min(0).max(10_000),
    defaultSize: projectsProjectDetailLauncherSizeSchema,
    allowedSizes: z.array(projectsProjectDetailLauncherSizeSchema).min(1).max(4),
    label: z.string().trim().min(1).max(80).nullable(),
    description: z.string().trim().min(1).max(200).nullable(),
}).superRefine((value, context) => {
    if (new Set(value.allowedSizes).size !== value.allowedSizes.length) {
        context.addIssue({ code: 'custom', message: 'Allowed Project tile sizes must be unique.' });
    }
    if (!value.allowedSizes.includes(value.defaultSize)) {
        context.addIssue({ code: 'custom', message: 'The default Project tile size must be allowed.' });
    }
});
export const projectsProjectDetailLauncherConfigurationValueSchema = z.strictObject({
    schemaVersion: z.literal(1),
    enabled: z.boolean(),
    allowUserPersonalization: z.boolean(),
    usageSuggestionsEnabled: z.boolean(),
    tileGapPx: z.number().int().min(8).max(32),
    minimumTileWidthPx: z.number().int().min(144).max(360),
    heading: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    customizeLabel: z.string().trim().min(1).max(80),
    saveLabel: z.string().trim().min(1).max(80),
    resetLabel: z.string().trim().min(1).max(80),
    historyLabel: z.string().trim().min(1).max(80),
    closeLabel: z.string().trim().min(1).max(80),
    rules: z.array(projectsProjectDetailLauncherRuleSchema).max(64),
}).superRefine((value, context) => {
    const moduleIds = value.rules.map(({ moduleId }) => moduleId);
    if (new Set(moduleIds).size !== moduleIds.length) {
        context.addIssue({ code: 'custom', message: 'Project tile module rules must be unique.' });
    }
});
export const projectsProjectDetailLauncherConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'projects.detail-launcher.configuration',
        moduleId: 'projects',
        title: 'Project Snapshot module tiles',
        helpText: 'Controls tenant-visible Project module tiles, default order and size, personalization bounds, layout, and copy.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            schemaVersion: 1,
            enabled: true,
            allowUserPersonalization: true,
            usageSuggestionsEnabled: false,
            tileGapPx: 16,
            minimumTileWidthPx: 176,
            heading: 'Project Snapshot',
            description: 'Open the tools available for this Project.',
            customizeLabel: 'Customize tiles',
            saveLabel: 'Save my layout',
            resetLabel: 'Reset my layout',
            historyLabel: 'Layout history',
            closeLabel: 'Close',
            rules: [],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: projectsProjectDetailLauncherConfigurationValueSchema,
});
export const projectsProjectDetailLauncherPreferenceValueSchema = z.strictObject({
    schemaVersion: z.literal(1),
    orderedModuleIds: z.array(projectsProjectDetailLauncherModuleIdSchema).max(64),
    hiddenModuleIds: z.array(projectsProjectDetailLauncherModuleIdSchema).max(64),
    sizes: z.record(projectsProjectDetailLauncherModuleIdSchema, projectsProjectDetailLauncherSizeSchema),
}).superRefine((value, context) => {
    if (new Set(value.orderedModuleIds).size !== value.orderedModuleIds.length) {
        context.addIssue({ code: 'custom', message: 'Project tile order must contain unique modules.' });
    }
    if (new Set(value.hiddenModuleIds).size !== value.hiddenModuleIds.length) {
        context.addIssue({ code: 'custom', message: 'Hidden Project tiles must contain unique modules.' });
    }
});
export const projectsProjectDetailLauncherPreferenceSetting = definePlatformSetting({
    definition: {
        key: 'projects.detail-launcher.preference',
        moduleId: 'projects',
        title: 'My Project Snapshot tile layout',
        helpText: 'Stores one user’s tile order, visibility, and sizes within the tenant’s published bounds.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            schemaVersion: 1,
            orderedModuleIds: [],
            hiddenModuleIds: [],
            sizes: {},
        },
        permittedScopes: ['user'],
        mergeStrategy: 'replace',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: false,
        userOverridable: true,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: projectsProjectDetailLauncherPreferenceValueSchema,
});
export const projectsSettingRegistrations = [
    projectsBoardConfigurationSetting,
    projectsOperationalConfigurationSetting,
    projectsBoardAppearanceSetting,
    projectsCloseoutConfigurationSetting,
    projectsFileConfigurationSetting,
    projectsCopySetting,
    projectsPortalDisplaySetting,
    projectsPortalCopySetting,
    projectsProjectDetailLauncherConfigurationSetting,
    projectsProjectDetailLauncherPreferenceSetting,
];
//# sourceMappingURL=projects-setting-definitions.js.map